// BLOQ Talk — Quantum entropy mixing for the PQC / Cipher Lock stack.
//
// Fetches quantum random bytes from our backend QRNG proxy (POST /api/qrng/seed on
// REACT_APP_BACKEND_URL — our own pod, NOT the external app API) and mixes them with the
// browser CSPRNG via HKDF-SHA-256 to produce seeds for key/nonce generation:
//
//     seed = HKDF-SHA256( salt=getRandomValues(32),
//                         IKM = getRandomValues(n) ‖ quantumBytes(n),
//                         info = "bloq-qrng-mix-v1", len = n )
//
// GUARANTEES
//  • FAIL-OPEN: any proxy error/timeout → pure crypto.getRandomValues(n). Quantum entropy
//    can only ADD to local randomness — it can never weaken it or block key generation.
//  • Because the browser CSPRNG is always part of the IKM, the output is unpredictable even
//    if the quantum source were ever wrong/compromised (defense-in-depth).
//  • Bytes are pooled and used ONCE, with a background refill, per QeM's EaaS guidance
//    (don't hit the service per keygen).
//
// ⚠️  The backend is currently a MOCK source (NOT real quantum) until the QeM EaaS .so +
//     creds land; the proxy reports `quantum:false` in mock mode, so `quantum` below stays
//     false and the "Quantum-seeded" trust badge will NOT show until the real source is live.
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POOL_TARGET = 256; // bytes to keep buffered
const FETCH_TIMEOUT_MS = 2500;

let pool = new Uint8Array(0);
let poolOffset = 0;
let sourceQuantum = false; // was the last successful fetch real quantum?
let inflight = null;

function remaining() {
  return pool.length - poolOffset;
}

function rand(n) {
  return globalThis.crypto.getRandomValues(new Uint8Array(n));
}

async function fetchQuantum(n) {
  if (!BACKEND) throw new Error("no backend");
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("bloq_token")) || "";
    const res = await fetch(`${BACKEND}/api/qrng/seed`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ bytes: n }),
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`qrng ${res.status}`);
    const j = await res.json();
    const bytes = Uint8Array.from(atob(j.bytes_b64), (c) => c.charCodeAt(0));
    return { bytes, quantum: !!j.quantum };
  } finally {
    clearTimeout(t);
  }
}

// Refill the pool (best-effort, single-flight). Never throws.
async function refill(min) {
  if (inflight) return inflight;
  const want = Math.max(POOL_TARGET, min);
  inflight = (async () => {
    try {
      const { bytes, quantum } = await fetchQuantum(want);
      const leftover = pool.slice(poolOffset);
      const merged = new Uint8Array(leftover.length + bytes.length);
      merged.set(leftover, 0);
      merged.set(bytes, leftover.length);
      pool = merged;
      poolOffset = 0;
      sourceQuantum = quantum;
    } catch {
      sourceQuantum = false; // quantum unavailable → we'll fall back to local CSPRNG
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function takeFromPool(n) {
  if (remaining() < n) return null;
  const out = pool.slice(poolOffset, poolOffset + n);
  poolOffset += n;
  // Background top-up when running low (don't await).
  if (remaining() < POOL_TARGET / 2) refill(POOL_TARGET);
  return out;
}

/** Pre-fetch quantum bytes so the first keygen doesn't pay the network round-trip. */
export async function prewarmQuantum() {
  if (remaining() < POOL_TARGET / 2) await refill(POOL_TARGET);
}

/**
 * Return `n` seed bytes = HKDF(local ‖ quantum) when quantum is available, else local CSPRNG.
 * @returns {Promise<{ bytes: Uint8Array, quantum: boolean }>} `quantum` is true ONLY when real
 *          quantum entropy contributed (always false while the backend is the mock source).
 */
export async function seedBytes(n) {
  const local = rand(n);
  let q = takeFromPool(n);
  if (!q) {
    await refill(n); // one best-effort fetch, then retry the pool
    q = takeFromPool(n);
  }
  if (q && sourceQuantum) {
    const ikm = new Uint8Array(local.length + q.length);
    ikm.set(local, 0);
    ikm.set(q, local.length);
    const info = new TextEncoder().encode("bloq-qrng-mix-v1");
    const mixed = hkdf(sha256, ikm, rand(32), info, n);
    return { bytes: mixed, quantum: true };
  }
  return { bytes: local, quantum: false };
}

/** Current entropy-source status (drives the "Quantum-seeded" trust signal). */
export function quantumSourceStatus() {
  return { quantum: sourceQuantum };
}
